import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XMLParser } from "fast-xml-parser";
import GoogleMapReact from "google-map-react";
import JSZip from "jszip";
import { TextField, Grid, InputLabel, Select, MenuItem, FormControl, FormHelperText } from "@mui/material";
import { Attachment, Check, Map } from "@mui/icons-material";
import CardTable from "../../components/CardTable/CardTable";
import RegularButton from "../../components/CustomButtons/Button";
import { polygonColumns } from "./columns";
import { useForm } from "../../hooks/useForm";
import { useSearch } from "../../hooks/useSearch";
import {
  fetchCoverages,
  createNewCoverage,
  editCoverage,
  setStationBrands
} from "../../redux/actions/coverages/coveragesActions";

const MAPS_API_KEY = process.env.REACT_APP_MAPS_API_KEY;

export const FormCoverageEdit = (props) => {
  const { handleClose, id_station } = props;
  const { zoneTypeList, productTypeList, addMode, coverageSelected, brandList } = useSelector(state => state.coverages);
  const { stationsList } = useSelector(state => state.companies);
  const { brands } = useSelector((state) => state.brandsReducer);
  const { inputValues, handleInputOnChange, handleNewValues, runValidates, fieldErrors } = useForm({
    formStep: 1,
    polygon: {},
    name: '',
    description: '',
    brand: '',
    type: '',
    productType: '',
    status: 'ACTIVE'
  }, {
    type: { required: true },
    productType: { required: true },
    brand: { required: true },
    name: { required: !addMode },
  })
  const { name, description, type, productType, status, formStep, polygon, brand } = inputValues;
  const { setSnackbar } = useSearch();
  const fileKMZInputRef = useRef();
  const [polygons, setPolygons] = useState([]);
  const polygonRef = React.useRef(null);
  const listenersRef = React.useRef([]);

  const dispatch = useDispatch();


  const reloadElements = () => {
    dispatch(fetchCoverages(id_station));
    handleClose();
  };

  const handleSubmit = () => {
    if (formStep === 2) {
      if (runValidates()) return;

      setPolygons(polygons.map(obj => {
        if (obj.id === polygon.id) {
          obj.complete = <Check color="success" />;
          obj.type = type;
          obj.brand = brand;
          obj.productType = productType;
        }
        return obj;
      }));
      return handleNewValues({ formStep: 1 });
    }

    if (!addMode && !name) return setSnackbar({ msg: 'Debe ingresar un nombre', isOpen: true });
    if (addMode && !polygons.length) return setSnackbar({ msg: 'Debe seleccionar un archivo KML/KMZ', isOpen: true });
    if (addMode && polygons.some(obj => !obj.complete)) return setSnackbar({ msg: "Hay polígonos que aun no estan configurados", isOpen: true });

    const form = {
      name,
      description,
      status,
      polygons,
      id_station
    };

    if (!addMode) {
      const coords = polygon.coords;
      const isClosed = coords.length > 1
        && coords[0].lat === coords[coords.length - 1].lat
        && coords[0].lng === coords[coords.length - 1].lng;

      const closedCoords = isClosed ? coords : [...coords, { ...coords[0] }];
      form.polygons = [{ ...polygon, coords: closedCoords }];
      form.brand = brand;
      form.product_type = productType;
      form.type = type;
    }

    if (addMode) {
      dispatch(createNewCoverage(form, reloadElements));
    } else {
      dispatch(editCoverage(coverageSelected.id, form, reloadElements));
    }
  };

  const handleEdit = (row) => {
    handleNewValues({ ...row, formStep: 2, polygon: row });
  };

  const handleRemove = (row) => {
    setPolygons(polygons.filter(obj => obj.id !== row.id));
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size >= 1500000) {
      return setSnackbar({ msg: "El archivo no puede pesar mas de 1.4Mb", isOpen: true });
    }

    try {
      const ext = file.name.toLowerCase().split(".").pop();
      let kmlText = null;

      if (ext === "kmz") {
        const zip = await JSZip.loadAsync(file);
        const kmlFile = Object.keys(zip.files).find((f) => f.endsWith(".kml"));

        if (!kmlFile) {
          return setSnackbar({ msg: "No se encontraron archivos .kml dentro del archivo .kmz", isOpen: true });
        }

        kmlText = await zip.files[kmlFile].async("text");
      }

      if (ext === "kml") {
        kmlText = await file.text();
      }

      if (!kmlText) {
        return setSnackbar({ msg: "Formato de archivo no válido. Solo se aceptan .kml o .kmz", isOpen: true });
      }

      const parser = new XMLParser({
        ignoreAttributes: false,
        attributeNamePrefix: "@_",
      });
      const json = parser.parse(kmlText);

      const extractPlacemarks = (node) => {
        if (!node) return [];

        let found = [];

        if (node.Placemark) {
          if (Array.isArray(node.Placemark)) {
            found.push(...node.Placemark);
          } else {
            found.push(node.Placemark);
          }
        }

        if (node.Folder) {
          const folders = Array.isArray(node.Folder)
            ? node.Folder
            : [node.Folder];

          folders.forEach((folder) => {
            found.push(...extractPlacemarks(folder));
          });
        }

        return found;
      };

      const placemarks = extractPlacemarks(json.kml?.Document);
      const extracted = placemarks
        .filter((pm) => pm?.Polygon?.outerBoundaryIs?.LinearRing?.coordinates)
        .map((pm, idx) => {
          const coordsText =
            pm.Polygon.outerBoundaryIs.LinearRing.coordinates.trim()

          const coords = coordsText.split(/\s+/).map((pair) => {
            const [lng, lat] = pair.split(",").map(Number);
            return { lat, lng };
          });

          return {
            id: idx,
            coverageName: pm.name?.substr(0, 100) || "Unnamed",
            coords,
            edit: handleEdit,
            remove: handleRemove,
            complete: false,
            type: null,
            brand: null,
            productType: null,
          };
        });

      setPolygons(extracted);
    } catch (err) {
      setSnackbar({ msg: "Error al leer el archivo KML/KMZ", isOpen: true });
    }
  };

  const handleKMZButtonClick = () => fileKMZInputRef.current.click();

  const handleOnChange = (e) => {
    handleInputOnChange(e);
  };

  const handlePolygonChange = (newCoords) => {
    handleNewValues({ polygon: { coords: newCoords } });
  };

  const defaultCenter = React.useMemo(() => {
    if (polygon?.coords?.length) {
      return {
        lat: polygon.coords[0].lat,
        lng: polygon.coords[0].lng,
      };
    }

    return { lat: 20.67, lng: -103.35 };
  }, [polygon?.coords]);

  const handleGoogleApiLoaded = React.useCallback(
    ({ map, maps }) => {
      if (!map || !maps || !maps.Polygon) return;

      maps.event.addListenerOnce(map, "idle", () => {
        const coverageMap = new maps.Polygon({
          paths: polygon.coords,
          editable: !addMode,
          strokeColor: "#FF0000",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: "#FF0000",
          fillOpacity: 0.2,
          map,
        });

        const updatePolygon = () => {
          handlePolygonChange(
            coverageMap
              .getPath()
              .getArray()
              .map((p) => ({
                lat: p.lat(),
                lng: p.lng(),
              }))
          );
        };

        polygonRef.current = coverageMap;

        const path = coverageMap.getPath();

        listenersRef.current.push(
          maps.event.addListener(path, "set_at", updatePolygon)
        );

        listenersRef.current.push(
          maps.event.addListener(path, "insert_at", updatePolygon)
        );

        listenersRef.current.push(
          maps.event.addListener(path, "remove_at", updatePolygon)
        );
      });
    },
    [polygon.coords, addMode, handlePolygonChange]
  );

  React.useEffect(() => {
    return () => {
      if (window.google?.maps?.event) {
        listenersRef.current.forEach((listener) => {
          window.google.maps.event.removeListener(listener);
        });
      }

      listenersRef.current = [];

      if (polygonRef.current) {
        polygonRef.current.setMap(null);
        polygonRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!addMode) {
      handleNewValues({
        ...inputValues,
        name: coverageSelected?.name,
        description: coverageSelected?.description,
        type: coverageSelected?.type,
        productType: coverageSelected?.product_type,
        status: coverageSelected?.status,
        brand: coverageSelected?.id_brand,
        polygon: { coords: coverageSelected?.polygon?.[0]?.map(obj => ({ lat: obj.x, lng: obj.y })) || [] }
      });
    }
  }, [coverageSelected]);

  useEffect(() => {
    if (brands && id_station) {
      const uniqueBrands = Object.values(brands)
        .filter(brand => brand.idStation === id_station)
        .reduce((acc, brand) => {
          if (!acc.find(b => b.idBrand === brand.idBrand)) {
            acc.push({ ...brand, value: brand.idBrand });
          }
          return acc;
        }, [])
        .sort((a, b) => a.name.localeCompare(b.name));
      dispatch(setStationBrands(uniqueBrands));
    }
  }, [brands, id_station]);

  return (
    <>
      <FormControl fullWidth onSubmit={handleSubmit}>
        <Grid container spacing={1} style={{ paddingRight: 0 }}>
          {
            formStep === 1 &&
            <>
              {!addMode && (
                <Grid item xs={12} sm={12}>
                  <FormControl variant="outlined" fullWidth >
                    <InputLabel htmlFor="outlined-age-native-simple">Estatus *</InputLabel>
                    <Select
                      value={status}
                      name='status'
                      onChange={handleOnChange}
                      label="Estatus"
                      size="small"
                    >
                      <MenuItem value='ACTIVE'>Activo</MenuItem>
                      <MenuItem value='INACTIVE'>Inactivo</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {addMode &&
                <Grid item xs={12} sm={12}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Archivo KML/KMZ *</InputLabel>
                    <RegularButton
                      color="info"
                      justIcon
                      round
                      size="sm"
                      tooltip="Adjuntar KML/KMZ"
                      onClick={handleKMZButtonClick}
                      style={{ alignSelf: 'end', marginTop: '10px' }}
                    >
                      <Attachment style={{ margin: 0 }} />
                      <input
                        type="file"
                        accept='.kmz'
                        style={{ display: "none" }}
                        ref={fileKMZInputRef}
                        onChange={handleFileChange}
                      />
                    </RegularButton>
                  </FormControl>
                </Grid>
              }

              {!addMode &&
                <Grid item xs={12} sm={12}>
                  <TextField
                    type="text"
                    label="Nombre *"
                    name="name"
                    size="small"
                    value={name}
                    onChange={handleOnChange}
                    error={fieldErrors.name}
                    helperText={fieldErrors.name || ""}
                    variant="outlined"
                    inputProps={{
                      maxLength: 100
                    }}
                    fullWidth
                  />
                </Grid>
              }
              <Grid item xs={12} sm={12}>
                <TextField
                  type="text"
                  label="Descripción"
                  name="description"
                  value={description}
                  onChange={handleOnChange}
                  variant="outlined"
                  inputProps={{
                    maxLength: 98
                  }}
                  size="small"
                  fullWidth
                />
              </Grid>
              {addMode &&
                <Grid item xs={12} sm={12}>
                  <CardTable
                    data={polygons}
                    columns={polygonColumns(handleRemove)}
                    icon={<Map />}
                  />
                </Grid>
              }
            </>
          }
          {
            formStep === 2 || !addMode ?
              <>
                <Grid item xs={12} sm={12}>
                  <TextField
                    type="text"
                    label="Planta"
                    name="stationName"
                    value={stationsList.find(obj => obj.id === id_station)?.name}
                    variant="outlined"
                    disabled
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl variant="outlined" error={!!fieldErrors.type} fullWidth>
                    <InputLabel htmlFor="outlined-age-native-simple">Tipo de zona *</InputLabel>
                    <Select
                      value={type}
                      name='type'
                      onChange={handleOnChange}
                      label="Tipo de Zona"
                      size="small"
                    >
                      <MenuItem value=''>Seleccione un tipo de zona</MenuItem>
                      {
                        zoneTypeList.map(({ label, value }) => (
                          <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))
                      }
                    </Select>

                    <FormHelperText style={{ color: 'red' }}>{fieldErrors.type || ""}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl variant="outlined" error={!!fieldErrors.productType} fullWidth>
                    <InputLabel htmlFor="outlined-age-native-simple">Tipo de producto *</InputLabel>
                    <Select
                      value={productType}
                      name='productType'
                      onChange={handleOnChange}
                      label="Tipo de producto"
                      size="small"
                    >
                      <MenuItem value=''>Seleccione un tipo de producto</MenuItem>
                      {
                        productTypeList.map(({ label, value }) => (
                          <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))
                      }
                    </Select>
                    <FormHelperText style={{ color: 'red' }}>{fieldErrors.productType || ""}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12}>
                  <FormControl variant="outlined" error={!!fieldErrors.brand} fullWidth>
                    <InputLabel htmlFor="outlined-age-native-simple">Marca *</InputLabel>
                    <Select
                      value={brand}
                      name='brand'
                      onChange={handleOnChange}
                      label="Marca"
                      size="small"
                    >
                      <MenuItem value=''>Seleccione una marca</MenuItem>
                      {
                        brandList.map(({ label, value }) => (
                          <MenuItem key={value} value={value}>{label}</MenuItem>
                        ))
                      }
                    </Select>
                    <FormHelperText style={{ color: 'red' }}>{fieldErrors.brand || ""}</FormHelperText>
                  </FormControl>
                </Grid>
                {polygons || polygon.coords ?
                  <Grid item xs={12} sm={12}>
                    <div style={{ height: "80vh", width: "100%", marginTop: "1rem" }}>
                      <GoogleMapReact
                        bootstrapURLKeys={{ key: MAPS_API_KEY }}
                        defaultCenter={defaultCenter}
                        defaultZoom={12}
                        yesIWantToUseGoogleMapApiInternals
                        onGoogleApiLoaded={handleGoogleApiLoaded}
                      />
                    </div>
                  </Grid>
                  : null}
              </> : null
          }
          <Grid item xs={12} sm={12} sx={{ display: 'flex', justifyContent: 'space-between' }}>

            <RegularButton size="sm" color="transparent" onClick={handleClose}>Cancelar</RegularButton>
            {
              formStep === 2 &&
              <RegularButton size="sm" color="danger" onClick={() => handleNewValues({ formStep: 1 })}>Regresar</RegularButton>
            }
            <RegularButton
              color="info"
              size="sm"
              onClick={handleSubmit}
            >
              {formStep === 1 ? 'Finalizar' : 'Guardar'}
            </RegularButton>
          </Grid>
        </Grid>
      </FormControl>
    </>
  );
};
